(() => {
  const sectionSelector = '[data-en-savoir-plus]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');

  const slugify = (value) => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'section';

  const initSection = (root) => {
    if (!root || root.dataset.enSavoirInitialized === 'true') return;

    const content = root.querySelector('[data-en-savoir-content]');
    const summary = root.querySelector('[data-en-savoir-summary]');
    const summaryList = root.querySelector('[data-en-savoir-summary-list]');
    const toggle = root.querySelector('[data-en-savoir-toggle]');

    if (!content) return;

    root.dataset.enSavoirInitialized = 'true';

    const readMoreLabel = toggle?.textContent?.trim() || 'Lire plus';
    const readLessLabel = root.dataset.readLessLabel || 'Lire moins';

    content.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.decoding = 'async';
    });

    if (summary && summaryList) {
      summaryList.innerHTML = '';

      const headings = Array.from(content.querySelectorAll('h2, h3'));
      const usedIds = new Set();

      headings.forEach((heading) => {
        const baseId = heading.id || `${root.id}-${slugify(heading.textContent)}`;
        let uniqueId = baseId;
        let suffix = 2;

        while (usedIds.has(uniqueId)) {
          uniqueId = `${baseId}-${suffix}`;
          suffix += 1;
        }

        heading.id = uniqueId;
        usedIds.add(uniqueId);

        const item = document.createElement('li');
        const link = document.createElement('a');

        item.className = 'en-savoir-plus__summary-item';
        link.className = 'en-savoir-plus__summary-link';

        if (heading.tagName === 'H3') {
          link.classList.add('en-savoir-plus__summary-link--subheading');
        }

        link.href = `#${encodeURIComponent(uniqueId)}`;
        link.textContent = heading.textContent.trim();

        item.appendChild(link);
        summaryList.appendChild(item);
      });

      summary.hidden = headings.length === 0;
    }

    const getCollapsedHeight = () => {
      const mobileHeight = Number(root.dataset.mobileCollapsedHeight) || 360;
      const desktopHeight = Number(root.dataset.desktopCollapsedHeight) || 420;

      if (mobile.matches || !summary || summary.hidden) {
        root.style.removeProperty('--en-savoir-desktop-effective-height');
        return mobile.matches ? mobileHeight : desktopHeight;
      }

      // Keep the collapsed article and the rendered summary on the same baseline.
      const article = content.closest('.en-savoir-plus__article');
      const articleStyle = article ? window.getComputedStyle(article) : null;
      const toggleStyle = toggle ? window.getComputedStyle(toggle) : null;
      const articlePadding = articleStyle
        ? (Number.parseFloat(articleStyle.paddingTop) || 0) + (Number.parseFloat(articleStyle.paddingBottom) || 0)
        : 0;
      const toggleHeight = toggleStyle
        ? Math.max(toggle.scrollHeight, Number.parseFloat(toggleStyle.minHeight) || 0)
        : 0;
      const toggleSpace = toggleStyle
        ? Math.max(
          toggleHeight
            + (Number.parseFloat(toggleStyle.marginTop) || 0)
            + (Number.parseFloat(toggleStyle.marginBottom) || 0),
          0
        )
        : 0;
      const matchedHeight = Math.max(summary.getBoundingClientRect().height - articlePadding - toggleSpace, 1);
      const matchedHeightValue = `${matchedHeight.toFixed(2)}px`;

      if (root.style.getPropertyValue('--en-savoir-desktop-effective-height') !== matchedHeightValue) {
        root.style.setProperty('--en-savoir-desktop-effective-height', matchedHeightValue);
      }

      return matchedHeight;
    };

    const updateCollapsedState = () => {
      const collapsedHeight = getCollapsedHeight();
      const hasCollapsedContent = content.scrollHeight > collapsedHeight + 1;

      root.classList.toggle('has-collapsed-content', hasCollapsedContent);

      if (!toggle) return;

      toggle.hidden = !hasCollapsedContent;

      if (!hasCollapsedContent) {
        root.classList.remove('is-expanded');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = readMoreLabel;
        return;
      }

      const isExpanded = root.classList.contains('is-expanded');
      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      toggle.textContent = isExpanded ? readLessLabel : readMoreLabel;
    };

    const handleToggle = () => {
      const shouldExpand = !root.classList.contains('is-expanded');

      root.classList.toggle('is-expanded', shouldExpand);

      if (toggle) {
        toggle.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
        toggle.textContent = shouldExpand ? readLessLabel : readMoreLabel;
      }

      if (!shouldExpand) {
        const rootTop = root.getBoundingClientRect().top + window.scrollY;
        const offset = 120;

        window.scrollTo({
          top: Math.max(rootTop - offset, 0),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }
    };

    const handleSummaryClick = (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      const targetId = decodeURIComponent(link.hash.slice(1));
      const target = document.getElementById(targetId);
      if (!target) return;

      event.preventDefault();

      target.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start'
      });

      window.history.replaceState(null, '', `#${encodeURIComponent(targetId)}`);
    };

    toggle?.addEventListener('click', handleToggle);
    summary?.addEventListener('click', handleSummaryClick);
    mobile.addEventListener('change', updateCollapsedState);

    let resizeObserver = null;

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateCollapsedState);
      resizeObserver.observe(content);
      if (summary) resizeObserver.observe(summary);
    }

    updateCollapsedState();

    root.enSavoirCleanup = () => {
      resizeObserver?.disconnect();
      toggle?.removeEventListener('click', handleToggle);
      summary?.removeEventListener('click', handleSummaryClick);
      mobile.removeEventListener('change', updateCollapsedState);
      delete root.dataset.enSavoirInitialized;
      delete root.enSavoirCleanup;
    };
  };

  const watchSection = (root) => {
    if (!root || root.dataset.enSavoirObserved === 'true') return;

    if (!('IntersectionObserver' in window)) {
      initSection(root);
      return;
    }

    root.dataset.enSavoirObserved = 'true';

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      observer.disconnect();
      delete root.dataset.enSavoirObserved;
      initSection(root);
    }, {
      rootMargin: '400px 0px'
    });

    observer.observe(root);

    root.enSavoirObserverCleanup = () => {
      observer.disconnect();
      delete root.dataset.enSavoirObserved;
      delete root.enSavoirObserverCleanup;
    };
  };

  const initSections = (container = document) => {
    if (container.matches?.(sectionSelector)) {
      watchSection(container);
    }

    container.querySelectorAll?.(sectionSelector).forEach(watchSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections(), { once: true });
  } else {
    initSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(sectionSelector)
      ? event.target
      : event.target.querySelector?.(sectionSelector);

    root?.enSavoirObserverCleanup?.();
    root?.enSavoirCleanup?.();
  });
})();
